/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { ParamListBase } from '@react-navigation/native';
import {
  getConsultationById,
  listConsultationMessages,
  addConsultationReply,
  markConsultationReadByStore,
  closeConsultation,
  getPrescriptionSignedUrl,
  type ConsultationRow,
  type ConsultationMessage,
} from '../infra/consultationsRepo';
import { useConsultationRealtime } from '../hooks/useConsultationRealtime';
import { ZoomableImage } from '../components/ZoomableImage';
import { formatArPhoneDisplay } from '@shared/phone';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { ticketColors } from '../../shared/theme/ticketColors';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { showError } from '../../shared/ui/appToast'
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { status as statusTokens } from '@vuelto/tokens';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  answered: 'Respondida',
  closed: 'Cerrada',
};

const STATUS_COLORS: Record<string, string> = {
  pending: statusTokens.consultationPending,
  answered: statusTokens.consultationAnswered,
  closed: statusTokens.consultationClosed,
};

export function ConsultationDetailScreen() {
  const route = useRoute<RouteProp<ParamListBase, 'ConsultationDetail'>>();
  const consultationId = (route.params as { consultationId: string }).consultationId;
  const canReply = useModulePermission('BranchSettings', 'reply');
  const canViewSensitive = useModulePermission('BranchSettings', 'view_sensitive');
  const canDownload = useModulePermission('BranchSettings', 'download');
  const [consultation, setConsultation] = useState<ConsultationRow | null>(null);
  const [messages, setMessages] = useState<ConsultationMessage[]>([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const scrollRef = useRef<ScrollView>(null);
  const { confirm, ConfirmModal } = useOperationalConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, msgs] = await Promise.all([
        getConsultationById(consultationId),
        listConsultationMessages(consultationId),
      ]);
      setConsultation(c);
      setMessages(msgs);

      if (c?.prescription_image_url && canViewSensitive && canDownload) {
        const url = await getPrescriptionSignedUrl(c.prescription_image_url);
        setImageUrl(url);
      } else {
        setImageUrl(null);
      }

      if (c && !c.read_by_store_at) {
        // BUG-16: handle rejection instead of fire-and-forget
        void markConsultationReadByStore(consultationId).catch((e) =>
          console.warn('markConsultationReadByStore failed', e),
        );
      }
    } catch (e) {
      console.error('ConsultationDetailScreen load', e);
      setError('No se pudo cargar la consulta.');
    } finally {
      setLoading(false);
    }
  }, [consultationId, canViewSensitive, canDownload]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 100);
  }, [messages]);

  useConsultationRealtime(
    consultationId,
    (msg) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    },
    // BUG-09: update consultation state on status changes (e.g. customer closes)
    (updated) => {
      setConsultation((prev) => {
        if (!prev) return prev;
        return { ...prev, ...updated } as ConsultationRow;
      });
    },
  );

  const handleReply = async () => {
    if (!canReply) {
      showError('Sin permiso', 'No podés responder consultas.');
      return;
    }
    if (reply.trim().length < 1) return;
    setSending(true);
    try {
      await addConsultationReply(consultationId, reply.trim());
      setReply('');
      await load();
    } catch (e) {
      showError('Error', e instanceof Error ? e.message : 'No se pudo responder.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    const ok = await confirm({
      title: 'Cerrar consulta',
      message: '¿Seguro que querés cerrar esta consulta?',
      confirmLabel: 'Cerrar',
      destructive: true,
    });
    if (!ok) return;
    try {
      await closeConsultation(consultationId);
      await load();
    } catch (e) {
      showError('Error', e instanceof Error ? e.message : 'No se pudo cerrar.');
    }
  };

  if (loading) {
    return (
      <ShellContentFrame style={styles.center}>
        <Text style={styles.loadingText} testID="consultations.detail.loading">Cargando consulta…</Text>
      </ShellContentFrame>
    );
  }

  // BUG-13: show error state instead of perpetual loading
  if (error || !consultation) {
    return (
      <ShellContentFrame style={styles.center}>
        <Text style={styles.loadingText}>{error ?? 'No se encontró la consulta.'}</Text>
        <TouchableOpacity
          style={styles.retryBtn}
          onPress={() => void load()}
          accessibilityRole="button"
          accessibilityLabel="Reintentar carga de consulta"
        >
          <Text style={styles.retryBtnText}>Reintentar</Text>
        </TouchableOpacity>
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame style={styles.container}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={90}
      >
        <ScrollView
          ref={scrollRef}
          contentContainerStyle={{ paddingBottom: 16 }}
          refreshControl={
            <RefreshControl
              refreshing={loading}
              onRefresh={() => void load()}
              colors={[ticketColors.gold]}
              tintColor={ticketColors.gold}
            />
          }
        >
          {/* Header */}
          <View style={styles.headerCard}>
            <View style={styles.headerRow}>
              <Text style={styles.title}>{consultation.subject}</Text>
              <View style={[styles.statusBadge, { backgroundColor: STATUS_COLORS[consultation.status] ?? statusTokens.consultationClosed }]} testID="consultations.detail.status">
                <Text style={styles.statusText}>{STATUS_LABELS[consultation.status] ?? consultation.status}</Text>
              </View>
            </View>
            <Text style={styles.date}>{formatLocalDateTime(consultation.created_at)}</Text>
          </View>

          {/* Customer info */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle} testID="consultations.detail.section.cliente">Cliente</Text>
            <Text style={styles.customerName}>{consultation.customer_name}</Text>
            {consultation.customer_phone ? (
              <Text style={styles.customerPhone}>
                {formatArPhoneDisplay(consultation.customer_phone)}
              </Text>
            ) : null}
            {canViewSensitive && consultation.customer_dni ? (
              <Text style={styles.customerPhone} testID="consultations.detail.dni">
                DNI: {consultation.customer_dni}
              </Text>
            ) : null}
            {consultation.prescription_image_url && (!canViewSensitive || !canDownload) ? (
              <Text style={styles.customerPhone}>No podés ver la receta.</Text>
            ) : null}
          </View>

          {/* Original message */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle} testID="consultations.detail.section.consulta">Consulta</Text>
            <Text style={styles.messageText}>{consultation.body}</Text>
          </View>

          {/* Prescription image */}
          {imageUrl && imageUrl.startsWith('http') && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} testID="consultations.detail.section.receta">Receta</Text>
              <ZoomableImage uri={imageUrl} title="Receta" style={styles.prescriptionImage} testID="consultations.prescription" />
            </View>
          )}

          {/* Messages thread */}
          {messages.length > 0 && (
            <View style={styles.threadSection}>
              <Text style={styles.sectionTitle} testID="consultations.detail.section.mensajes">Mensajes</Text>
              {messages.map((msg) => (
                <View
                  key={msg.id}
                  style={[styles.messageBubble, msg.author === 'store' ? styles.storeBubble : styles.customerBubble]}
                >
                  <Text style={styles.messageAuthor}>
                    {msg.author === 'store' ? 'Farmacia' : consultation.customer_name}
                  </Text>
                  <Text style={styles.messageBody}>{msg.body}</Text>
                  <Text style={styles.messageTime}>
                    {new Date(msg.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </ScrollView>

        {/* Reply input */}
        {consultation.status !== 'closed' && canReply && (
          <View style={styles.replyBar}>
            <TextInput
              style={styles.replyInput}
              placeholder="Escribí tu respuesta…"
              value={reply}
              onChangeText={setReply}
              multiline
              maxLength={5000}
              accessibilityLabel="Escribir respuesta a la consulta"
              accessibilityHint="Escribí tu respuesta para el cliente"
              testID="consultations.detail.reply-input"
            />
            <TouchableOpacity
              style={[styles.sendBtn, (!reply.trim() || sending) && styles.sendBtnDisabled]}
              onPress={() => void handleReply()}
              disabled={!reply.trim() || sending}
              accessibilityRole="button"
              accessibilityLabel="Enviar respuesta"
              accessibilityHint={sending ? 'Enviando…' : 'Envía tu respuesta al cliente'}
              testID="consultations.detail.send"
            >
              <Text style={styles.sendBtnText}>{sending ? '…' : 'Enviar'}</Text>
            </TouchableOpacity>
          </View>
        )}

        {consultation.status !== 'closed' && !canReply ? (
          <View style={styles.replyBar}>
            <Text style={styles.customerPhone} testID="consultations.detail.no-permission">No podés responder.</Text>
          </View>
        ) : null}

        {consultation.status !== 'closed' && (
          <TouchableOpacity
            style={styles.closeBtn}
            onPress={() => void handleClose()}
            accessibilityRole="button"
            accessibilityLabel="Cerrar consulta"
            accessibilityHint="Cierra esta consulta. No se podrán enviar más mensajes."
            testID="consultations.detail.close"
          >
            <Text style={styles.closeBtnText}>Cerrar consulta</Text>
          </TouchableOpacity>
        )}
      </KeyboardAvoidingView>
      {ConfirmModal}
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: ticketColors.paper },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { fontSize: 16, color: ticketColors.inkLight },
  headerCard: { backgroundColor: ticketColors.card, borderRadius: 14, padding: 16, margin: 12, borderWidth: 1, borderColor: ticketColors.border },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: 18, fontWeight: '700', color: ticketColors.ink, flex: 1, marginRight: 8 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  statusText: { fontSize: 11, fontWeight: '700', color: '#fff' },
  date: { fontSize: 12, color: ticketColors.inkLight, marginTop: 4 },
  section: { backgroundColor: ticketColors.card, borderRadius: 14, padding: 16, marginHorizontal: 12, marginTop: 8, borderWidth: 1, borderColor: ticketColors.border },
  threadSection: { padding: 16, marginHorizontal: 12, marginTop: 8 },
  sectionTitle: { fontSize: 13, fontWeight: '700', color: ticketColors.inkLight, marginBottom: 4 },
  customerName: { fontSize: 16, fontWeight: '600', color: ticketColors.ink },
  customerPhone: { fontSize: 14, color: ticketColors.inkLight, marginTop: 2 },
  messageText: { fontSize: 14, color: ticketColors.ink },
  photoLink: { fontSize: 14, color: ticketColors.gold, marginTop: 4 },
  prescriptionImage: { width: '100%', height: 200, borderRadius: 8, marginTop: 8 },
  messageBubble: { borderRadius: 10, padding: 12, marginBottom: 8, maxWidth: '85%' },
  storeBubble: { backgroundColor: ticketColors.gold, alignSelf: 'flex-end' },
  customerBubble: { backgroundColor: ticketColors.card, alignSelf: 'flex-start', borderWidth: 1, borderColor: ticketColors.border },
  messageAuthor: { fontSize: 11, fontWeight: '700', color: ticketColors.inkLight, marginBottom: 2 },
  messageBody: { fontSize: 14, color: ticketColors.ink },
  messageTime: { fontSize: 10, color: ticketColors.inkLight, marginTop: 4, textAlign: 'right' },
  replyBar: { flexDirection: 'row', paddingHorizontal: 12, paddingVertical: 8, gap: 8, backgroundColor: ticketColors.paper2, borderTopWidth: 1, borderTopColor: ticketColors.border },
  replyInput: { flex: 1, borderWidth: 1, borderColor: ticketColors.border, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8, fontSize: 14, maxHeight: 80, backgroundColor: ticketColors.card },
  sendBtn: { backgroundColor: ticketColors.gold, borderRadius: 10, paddingHorizontal: 16, justifyContent: 'center' },
  sendBtnDisabled: { opacity: 0.5 },
  sendBtnText: { color: ticketColors.goldInk, fontSize: 16, fontWeight: '700' },
  closeBtn: { padding: 12, alignItems: 'center' },
  closeBtnText: { fontSize: 14, color: ticketColors.inkLight },
  retryBtn: { marginTop: 12, paddingHorizontal: 16, paddingVertical: 8, backgroundColor: ticketColors.gold, borderRadius: 8 },
  retryBtnText: { color: ticketColors.goldInk, fontSize: 14, fontWeight: '700' },
});
